import ActionLimitOrder from "@/components/automation/actions/ActionLimitOrder.vue";
import ActionSwap from "@/components/automation/actions/ActionSwap.vue";
import FilterMint from "@/components/automation/filters/FilterMint.vue";
import FilterNumber from "@/components/automation/filters/FilterNumber.vue";
import FilterPercentage from "@/components/automation/filters/FilterPercentage.vue";
import FilterWallet from "@/components/automation/filters/FilterWallet.vue";
import SignalIndicatorBO from "@/components/automation/signals/SignalIndicatorBO.vue";
import SignalIndicatorEMA from "@/components/automation/signals/SignalIndicatorEMA.vue";
import SignalIndicatorMACD from "@/components/automation/signals/SignalIndicatorMACD.vue";
import SignalIndicatorRSI from "@/components/automation/signals/SignalIndicatorRSI.vue";
import SignalIndicatorSMA from "@/components/automation/signals/SignalIndicatorSMA.vue";
import SignalTokenBurn from "@/components/automation/signals/SignalTokenBurn.vue";
import SignalTokenMint from "@/components/automation/signals/SignalTokenMint.vue";
import SignalTokenPrice from "@/components/automation/signals/SignalTokenPrice.vue";
import SignalTokenPriceChange from "@/components/automation/signals/SignalTokenPriceChange.vue";
import SignalTokenTrade from "@/components/automation/signals/SignalTokenTrade.vue";
import SignalTokenTransfer from "@/components/automation/signals/SignalTokenTransfer.vue";
import SignalTokenVolumeChange from "@/components/automation/signals/SignalTokenVolumeChange.vue";
import { web3 } from "@project-serum/anchor";
import { struct, u32, u8 } from "@solana/buffer-layout";
import { bool, publicKey, u64 } from "@solana/buffer-layout-utils";
import { RawAccount, RawMint, RawMultisig } from "@solana/spl-token";
import {
	EActionCategories,
	EActionLimitOrder,
	EActions,
	EActionTrade,
	ESignalCategories,
	ESignalCustom,
	ESignalIndicator,
	ESignals,
	ESignalToken,
	ISession,
	ISettings,
	IToken,
} from "./interfaces";
import { getMintImageURL } from "./utils";

export const DEFAULT_PER_PAGE = 10;

export const FLUXB_MINT = "FLUXBmPhT3Fd1EDVFdg46YREqHBeNypn1h4EbnTzWERX";
// export const USDC_MINT = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"; // SOLANA
export const USDC_MINT = "fUSDNGgHkZfwckbr5RLLvRbvqvRcTLdH9hcHJiq4jry"; // FOGO
export const USDT_MINT = "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB";
export const SOL_MINT = "So11111111111111111111111111111111111111112";
export const FOGO_MINT = "FoGo111111111111111111111111111111111111112"; // Native FOGO token
export const BERN_MINT = "CKfatsPMUf8SkiURsDXs7eK6GWb4Jsd6UDbs7twMCWxo";

export const FIRST_POOL_PUBKEY = new web3.PublicKey(
	"Ebbpz3PWLaQxj2oyK967RgEPbcPypjQCoZ3tpB4fwLsk"
);

export const TOKEN22_PROGRAM_ID = new web3.PublicKey(
	"TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb"
);
export const V1_PROGRAM_ID = new web3.PublicKey(
	"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA"
);

export const ERROR_MESSAGES = {
	exceededBalance: "Balance exceeded",
	emptyBalance: "Balance empty",
};

export const SETTINGS_DEFAULT: ISettings = {
	explorerOpens: "sidebar",
	explorer: "https://explorer.fogo.io",
	customRpcUrl: "",
	slippage: 20,
	validatorTip: null,
	priorityFeeMax: null,
	priorityLevel: "market",
	validatorLevel: "market",
	swapValues: {
		sell: [10, 15, 25, 50, 75, 100],
		buy: [0.001, 0.025, 0.05, 1],
	},
	instantBuy: false
};

export const SESSION_DEFAULT = {
	lastToken: SOL_MINT,
} as ISession;

export const SOL_TOKEN: IToken = {
	address: new web3.PublicKey(SOL_MINT),
	amount: 0,
	decimals: 9,
	image: getMintImageURL("FoGo111111111111111111111111111111111111112"),
	mint: SOL_MINT,
	name: "Wrapped FOGO",
	programID: V1_PROGRAM_ID,
	supply: BigInt(0),
	symbol: "FOGO",
	verified: true,
	extensions: [],
};

export const FOGO_TOKEN: IToken = {
	address: new web3.PublicKey(FOGO_MINT),
	amount: 0,
	decimals: 9,
	image: getMintImageURL(FOGO_MINT),
	mint: FOGO_MINT,
	name: "FOGO",
	programID: V1_PROGRAM_ID,
	supply: BigInt(0),
	symbol: "FOGO",
	verified: true,
	extensions: [],
};

export const FLUXB_TOKEN: IToken = {
	address: new web3.PublicKey(FLUXB_MINT),
	amount: 0,
	decimals: 5,
	image: getMintImageURL(FLUXB_MINT),
	mint: FLUXB_MINT,
	name: "FluxBot",
	programID: TOKEN22_PROGRAM_ID,
	supply: BigInt(0),
	symbol: "FLUXB",
	verified: true,
	extensions: [],
};

export const USDC_TOKEN: IToken = {
	address: new web3.PublicKey(USDC_MINT),
	amount: 0,
	decimals: 6,
	image: getMintImageURL(USDC_MINT),
	mint: USDC_MINT,
	name: "Fogo USD",
	programID: V1_PROGRAM_ID,
	supply: BigInt(0),
	symbol: "fUSD",
	verified: true,
	extensions: [],
};

export const MINT_LAYOUT = struct<RawMint>([
	u32("mintAuthorityOption"),
	publicKey("mintAuthority"),
	u64("supply"),
	u8("decimals"),
	bool("isInitialized"),
	u32("freezeAuthorityOption"),
	publicKey("freezeAuthority"),
]);

export const MINT_SIZE = MINT_LAYOUT.span;

export const ACCOUNT_LAYOUT = struct<RawAccount>([
	publicKey("mint"),
	publicKey("owner"),
	u64("amount"),
	u32("delegateOption"),
	publicKey("delegate"),
	u8("state"),
	u32("isNativeOption"),
	u64("isNative"),
	u64("delegatedAmount"),
	u32("closeAuthorityOption"),
	publicKey("closeAuthority"),
]);

export const ACCOUNT_SIZE = ACCOUNT_LAYOUT.span;

export const MULTISIG_LAYOUT = struct<RawMultisig>([
	u8("m"),
	u8("n"),
	bool("isInitialized"),
	publicKey("signer1"),
	publicKey("signer2"),
	publicKey("signer3"),
	publicKey("signer4"),
	publicKey("signer5"),
	publicKey("signer6"),
	publicKey("signer7"),
	publicKey("signer8"),
	publicKey("signer9"),
	publicKey("signer10"),
	publicKey("signer11"),
]);

export const MULTISIG_SIZE = MULTISIG_LAYOUT.span;

export const ACCOUNT_TYPE_SIZE = 1;

export const SIGNAL_CATEGORIES_INFO: {
	[i in ESignalCategories]: {
		icon: string;
		name: string;
		description: string;
	};
} = {
	[ESignalCategories.TOKEN]: {
		icon: "bi-coin",
		name: "Tokens",
		description:
			"Signals when a price or volume changes, trades or any other on-chain token event.",
	},
	// [ESignalCategories.TRADER]: {
	// 	icon: "bi-person-fill",
	// 	name: "Traders",
	// 	description: "Signals when a specific trader performs an on-chain action.",
	// },
	// [ESignalCategories.LENDING]: {
	// 	icon: "bi-bank2",
	// 	name: "Lending",
	// 	description: "Signals when a lending action is performed.",
	// },
	[ESignalCategories.INDICATOR]: {
		icon: "bi-pie-chart-fill",
		name: "Technical Indicators",
		description: "Common technical indicators to enhance trading.",
	},
	[ESignalCategories.CUSTOM]: {
		icon: "bi-terminal-fill",
		name: "Custom Logic",
		description: "Connect in any 3rd party data stream with ease.",
	},
	// [ESignalCategories.SOCIAL]: {
	// 	icon: "bi-twitter",
	// 	name: "Social",
	// 	description: "Social events such as tweets, retweets & follows.",
	// },
};

export const SIGNALS_INFO: {
	[i in ESignalCategories]: {
		[i in ESignals]?: {
			name: string;
			icon?: string;
			description: string;
			disabled?: boolean;
		};
	};
} = {
	[ESignalCategories.TOKEN]: {
		[ESignalToken.TRADE]: {
			icon: "bi-arrow-left-right",
			name: "Trade",
			description: "Signal when any trade occurs",
		},
		// [ESignalToken.BUY]: {
		// 	name: "Buy",
		// 	description:
		// 		"Signal when the specified token is purchased with another asset",
		// 	disabled: true,
		// },
		// [ESignalToken.SELL]: {
		// 	name: "Sell",
		// 	description:
		// 		"Signal when the specified token is sold for a stablecoin or SOL",
		// 	disabled: true,
		// },
		[ESignalToken.PRICE]: {
			icon: "bi-currency-dollar",
			name: "Price",
			description:
				"Signal when the current price of the specified token changes",
		},
		[ESignalToken.PRICE_CHANGE]: {
			icon: "bi-arrow-down-up",
			name: "Price Change",
			description:
				"Signal when the percentage price change of the token over a specified time period occurs",
		},
		[ESignalToken.VOLUME_CHANGE]: {
			icon: "bi-arrow-down-up",
			name: "Volume Change",
			description:
				"Signal when the percentage change in trading volume for the token over a specified time period occurs",
		},
		[ESignalToken.MINT]: {
			icon: "bi-bank",
			name: "Mint",
			description:
				"Signal when new tokens are minted, increasing the total supply",
		},
		[ESignalToken.BURN]: {
			icon: "bi-fire",
			name: "Burn",
			description: "Signal when tokens are burned, decreasing the total supply",
		},
		[ESignalToken.TRANSFER]: {
			icon: "bi-arrow-right",
			name: "Transfer",
			description: "Signal when tokens are transferred between wallets",
		},
	},
	// [ESignalCategories.TRADER]: {
	// 	[ESignalTrader.TRADE]: {
	// 		name: "Trade",
	// 		description: "Signal when a specified wallet trades any tokens",
	// 		disabled: true,
	// 	},
	// 	[ESignalTrader.BUY]: {
	// 		name: "Buy",
	// 		description: "Signal when a specified wallet purchases any token",
	// 		disabled: true,
	// 	},
	// 	[ESignalTrader.SELL]: {
	// 		name: "Sell",
	// 		description: "Signal when a specified wallet sells any token",
	// 		disabled: true,
	// 	},
	// 	[ESignalTrader.TRANSFER]: {
	// 		name: "Transfer",
	// 		description:
	// 			"Signal when a specified wallet transfers tokens to another address",
	// 		disabled: true,
	// 	},
	// },
	// [ESignalCategories.LENDING]: {
	// 	[ESignalLending.WITHDRAW]: {
	// 		name: "MarginFi Withdraw",
	// 		description: "Signal when a wallet withdraws tokens from MarginFi",
	// 		disabled: true,
	// 	},
	// 	[ESignalLending.DEPOSIT]: {
	// 		name: "MarginFi Deposit",
	// 		description: "Signal when a wallet deposits tokens into MarginFi",
	// 		disabled: true,
	// 	},
	// 	[ESignalLending.BORROW]: {
	// 		name: "MarginFi Borrow",
	// 		description: "Signal when a wallet borrows tokens on MarginFi",
	// 		disabled: true,
	// 	},
	// 	[ESignalLending.LIQUIDATE]: {
	// 		name: "MarginFi Liquidate",
	// 		description: "Signal when a wallet's loan on MarginFi is liquidated",
	// 		disabled: true,
	// 	},
	// 	[ESignalLending.REPAY]: {
	// 		name: "MarginFi Repay",
	// 		description: "Signal when a wallet repays a token loan on MarginFi",
	// 		disabled: true,
	// 	},
	// },
	[ESignalCategories.INDICATOR]: {
		[ESignalIndicator.SMA]: {
			icon: "bi-graph-up",
			name: "Simple Moving Average (SMA)",
			description:
				"Signal when the average price over a specified period changes",
		},
		[ESignalIndicator.EMA]: {
			icon: "bi-graph-up",
			name: "Exponential Moving Average (EMA)",
			description:
				"Signal when the weighted moving average that gives more weight to recent prices changes",
		},
		// [ESignalIndicator.OBV]: {
		// 	name: "On-Balance Volume (OBV)",
		// 	description:
		// 		"Signal when the cumulative buying and selling pressure indicator changes",
		// 	disabled: true,
		// },
		[ESignalIndicator.RSI]: {
			icon: "bi-graph-up",
			name: "Relative Strength Index (RSI)",
			description:
				"Signal when the speed and change of price movements indicator, oscillating between 0 and 100, changes",
		},
		[ESignalIndicator.MACD]: {
			icon: "bi-graph-up",
			name: "Moving Average Convergence Divergence (MACD)",
			description:
				"Signal when the relationship between two moving averages of a security's price changes",
		},
		// [ESignalIndicator.ADI]: {
		// 	name: "Average Directional Index",
		// 	description: "Signal when the strength of a trend indicator changes",
		// 	disabled: true,
		// },
		// [ESignalIndicator.SO]: {
		// 	name: "Stochastic Oscillator",
		// 	description:
		// 		"Signal when the comparison of a closing price to its price range over a certain period changes",
		// 	disabled: true,
		// },
		// [ESignalIndicator.AO]: {
		// 	name: "Aroon Oscillator",
		// 	description:
		// 		"Signal when the measure of time between highs and lows over a time period changes",
		// 	disabled: true,
		// },
		[ESignalIndicator.BO]: {
			icon: "bi-graph-up",
			name: "Bollinger Bands",
			description:
				"Signal to determine where prices are high and low relative to each other",
		},
	},
	[ESignalCategories.CUSTOM]: {
		[ESignalCustom.WEBHOOK]: {
			name: "Webhook",
			description:
				"Signal when custom data is received from external sources via a webhook",
			disabled: true,
		},
	},
	// [ESignalCategories.SOCIAL]: {
	// 	[ESignalSocial.TWEET]: {
	// 		name: "User Tweet",
	// 		description: "Signal when a specified user posts a new tweet",
	// 		disabled: true,
	// 	},
	// 	[ESignalSocial.RETWEET]: {
	// 		name: "User Retweet",
	// 		description: "Signal when a specified user retweets a post",
	// 		disabled: true,
	// 	},
	// 	[ESignalSocial.QUOTE]: {
	// 		name: "User Quote",
	// 		description: "Signal when a specified user quotes another tweet",
	// 		disabled: true,
	// 	},
	// 	[ESignalSocial.NEWS]: {
	// 		name: "News",
	// 		description:
	// 			"Signal when specific news events or keywords appear from selected sources",
	// 		disabled: true,
	// 	},
	// },
};

export const SIGNALS_OUTPUT: {
	[i in ESignalCategories]: {
		[i in ESignals]?: {
			[field: string]: string[];
		};
	};
} = {
	[ESignalCategories.TOKEN]: {
		[ESignalToken.TRADE]: {
			wallet: ["Wallet", "PublicKey"],
			inputMint: ["Input Mint", "PublicKey"],
			outputMint: ["Output Mint", "PublicKey"],
			inAmount: ["In Amount", "uint64"],
			minimumOut: ["Minimum Amount Out", "uint64"],
			value: ["Value", "float64"],
		},
		[ESignalToken.BUY]: {
			wallet: ["Wallet", "PublicKey"],
			amount: ["Amount", "uint64"],
			value: ["Value", "float64"],
		},
		[ESignalToken.SELL]: {
			wallet: ["Wallet", "PublicKey"],
			amount: ["Amount", "uint64"],
			value: ["Value", "float64"],
		},
		[ESignalToken.PRICE]: {
			price: ["Price", "float64"],
		},
		[ESignalToken.PRICE_CHANGE]: {
			price: ["Price", "float64"],
			change_pct_1: ["Percentage of Variation in 1 Minute", "float64"],
			change_pct_5: ["Percentage of Variation in 5 Minutes", "float64"],
			change_pct_30: ["Percentage of Variation in 30 Minutes", "float64"],
			change_pct_1h: ["Percentage of Variation in 1 Hour", "float64"],
			change_pct_6h: ["Percentage of Variation in 6 Hours", "float64"],
			change_pct_12h: ["Percentage of Variation in 12 Hours", "float64"],
			change_pct_24h: ["Percentage of Variation in 24 Hours", "float64"],
		},
		[ESignalToken.VOLUME_CHANGE]: {
			change_pct_1: ["Percentage of Variation in 1 Minute", "float64"],
			change_pct_5: ["Percentage of Variation in 5 Minutes", "float64"],
			change_pct_30: ["Percentage of Variation in 30 Minutes", "float64"],
			change_pct_1h: ["Percentage of Variation in 1 Hour", "float64"],
			change_pct_6h: ["Percentage of Variation in 6 Hours", "float64"],
			change_pct_12h: ["Percentage of Variation in 12 Hours", "float64"],
			change_pct_24h: ["Percentage of Variation in 24 Hours", "float64"],
		},
		[ESignalToken.MINT]: {
			amount: ["Amount", "uint64"],
			oldSupply: ["Old Supply", "uint64"],
			newSupply: ["New Supply", "uint64"],
		},
		[ESignalToken.BURN]: {
			amount: ["Amount", "uint64"],
			oldSupply: ["Old Supply", "uint64"],
			newSupply: ["New Supply", "uint64"],
		},
		[ESignalToken.TRANSFER]: {
			amount: ["Amount", "uint64"],
			srcWallet: ["Source Wallet", "PublicKey"],
			dstWallet: ["Destination Wallet", "PublicKey"],
		},
	},
	// [ESignalCategories.TRADER]: {
	// 	[ESignalTrader.BUY]: {
	// 		mint: ["Mint", "PublicKey"],
	// 		amount: ["Amount", "uint64"],
	// 	},
	// 	[ESignalTrader.SELL]: {
	// 		mint: ["Mint", "PublicKey"],
	// 		amount: ["Amount", "uint64"],
	// 	},
	// 	[ESignalTrader.TRADE]: {
	// 		inputMint: ["Input Mint", "PublicKey"],
	// 		outputMint: ["Output Mint", "PublicKey"],
	// 		inAmount: ["In Amount", "uint64"],
	// 		minimumOut: ["Minimum Amount Out", "uint64"],
	// 	},
	// 	[ESignalTrader.TRANSFER]: {
	// 		amount: ["Amount", "uint64"],
	// 		srcWallet: ["Source Wallet", "PublicKey"],
	// 		dstWallet: ["Destination Wallet", "PublicKey"],
	// 	},
	// },
	// [ESignalCategories.LENDING]: {
	// 	[ESignalLending.BORROW]: {
	// 		wallet: ["Wallet", "PublicKey"],
	// 		mint: ["Mint", "PublicKey"],
	// 		amount: ["Amount", "uint64"],
	// 	},
	// 	[ESignalLending.DEPOSIT]: {
	// 		wallet: ["Wallet", "PublicKey"],
	// 		mint: ["Mint", "PublicKey"],
	// 		amount: ["Amount", "uint64"],
	// 	},
	// 	[ESignalLending.LIQUIDATE]: {
	// 		wallet: ["Wallet", "PublicKey"],
	// 		mint: ["Mint", "PublicKey"],
	// 		amount: ["Amount", "uint64"],
	// 	},
	// 	[ESignalLending.REPAY]: {
	// 		wallet: ["Wallet", "PublicKey"],
	// 		mint: ["Mint", "PublicKey"],
	// 		amount: ["Amount", "uint64"],
	// 	},
	// 	[ESignalLending.WITHDRAW]: {
	// 		wallet: ["Wallet", "PublicKey"],
	// 		mint: ["Mint", "PublicKey"],
	// 		amount: ["Amount", "uint64"],
	// 	},
	// },
	[ESignalCategories.INDICATOR]: {
		[ESignalIndicator.SMA]: {
			mint: ["Mint", "PublicKey"],
			sma: ["SMA", "float64"],
		},
		[ESignalIndicator.EMA]: {
			mint: ["Mint", "PublicKey"],
			ema: ["EMA", "float64"],
		},
		[ESignalIndicator.RSI]: {
			mint: ["Mint", "PublicKey"],
			rsi: ["RSI", "float64"],
		},
		[ESignalIndicator.MACD]: {
			mint: ["Mint", "PublicKey"],
			macd: ["MACD", "float64"],
			signal: ["Signal", "float64"],
		},
		[ESignalIndicator.BO]: {
			mint: ["Mint", "PublicKey"],
			upper: ["Upper Points", "float64"],
			middle: ["Middle Points", "float64"],
			lower: ["Lower Points", "float64"],
		},
	},
	// [ESignalCategories.SOCIAL]: {},
	[ESignalCategories.CUSTOM]: {
		[ESignalCustom.WEBHOOK]: {
			url: ["URL", "string"],
		},
	},
};

export const ACTION_CATEGORIES_INFO: {
	[i in EActionCategories]: {
		icon: string;
		name: string;
		description: string;
	};
} = {
	[EActionCategories.TRADE]: {
		icon: "bi-graph-up-arrow",
		name: "Trade",
		description:
			"Signals when a price or volume changes, trades or any other on-chain token event",
	},
	[EActionCategories.LIMIT_ORDER]: {
		icon: "bi-arrow-down-up",
		name: "Limit Orders",
		description: "Signals when a specific trader performs an on-chain action",
	},
};

export const ACTIONS_INFO: {
	[i in EActionCategories]: {
		[i in EActions]?: {
			name: string;
			description: string;
			disabled?: boolean;
		};
	};
} = {
	[EActionCategories.TRADE]: {
		[EActionTrade.SWAP]: {
			name: "Swap",
			description: "Swap tokens",
		},
		[EActionTrade.BUY]: {
			name: "Buy",
			description: "Buy a token",
		},
		[EActionTrade.SELL]: {
			name: "Sell",
			description: "Sell a token",
		},
	},
	[EActionCategories.LIMIT_ORDER]: {
		[EActionLimitOrder.LIMIT_ORDER]: {
			name: "Limit Order Buy",
			description:
				"Buy a token when the price drops below the specified threshold",
		},
		[EActionLimitOrder.TAKE_PROFIT]: {
			name: "Take Profit",
			description:
				"Sell a token when the price rises above the specified threshold",
			disabled: true,
		},
		[EActionLimitOrder.STOP_LOSS]: {
			name: "Stop Loss",
			description:
				"Sell a token when the price rises below the specified threshold",
			disabled: true,
		},
		[EActionLimitOrder.TRAILING_STOP_LOSS]: {
			name: "Trailing Stop Loss",
			description:
				"Sell a token when the price rises above the specified threshold and then falls by a % below the high price",
			disabled: true,
		},
	},
};

export const SIGNALS_COMPONENTS: {
	[i in ESignalCategories]: {
		[i in ESignals]?: any;
	};
} = {
	[ESignalCategories.TOKEN]: {
		[ESignalToken.TRADE]: SignalTokenTrade,
		[ESignalToken.PRICE]: SignalTokenPrice,
		[ESignalToken.PRICE_CHANGE]: SignalTokenPriceChange,
		[ESignalToken.VOLUME_CHANGE]: SignalTokenVolumeChange,
		[ESignalToken.MINT]: SignalTokenMint,
		[ESignalToken.BURN]: SignalTokenBurn,
		[ESignalToken.TRANSFER]: SignalTokenTransfer,
	},
	// [ESignalCategories.TRADER]: {},
	// [ESignalCategories.LENDING]: {},
	[ESignalCategories.INDICATOR]: {
		[ESignalIndicator.SMA]: SignalIndicatorSMA,
		[ESignalIndicator.EMA]: SignalIndicatorEMA,
		[ESignalIndicator.RSI]: SignalIndicatorRSI,
		[ESignalIndicator.MACD]: SignalIndicatorMACD,
		[ESignalIndicator.BO]: SignalIndicatorBO,
	},
	// [ESignalCategories.SOCIAL]: {},
	[ESignalCategories.CUSTOM]: {},
};

export const ACTIONS_COMPONENTS: {
	[i in EActionCategories]: {
		[i in EActions]?: any;
	};
} = {
	[EActionCategories.TRADE]: {
		[EActionTrade.SWAP]: ActionSwap,
		[EActionTrade.BUY]: ActionSwap,
		[EActionTrade.SELL]: ActionSwap,
	},
	[EActionCategories.LIMIT_ORDER]: {
		[EActionLimitOrder.LIMIT_ORDER]: ActionLimitOrder,
		[EActionLimitOrder.TAKE_PROFIT]: ActionLimitOrder,
		[EActionLimitOrder.STOP_LOSS]: ActionLimitOrder,
	},
};

export const FILTERS: Object = {
	int: FilterNumber,
	float: FilterPercentage,
	mint: FilterMint,
	wallet: FilterWallet,
};
